import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import CryptoJS from 'crypto-js';
import {
    ArrowLeft,
    Wallet,
    ChevronDown,
    CheckCircle2,
    Loader2,
    QrCode as QrCodeIcon,
    Lock,
    User,
    ShieldCheck,
    Share2,
    Info,
    Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSession } from '../../providers/SessionProvider';
import { comptesApi } from '../../api/clients.api';
import fondImageMobile from "../../components/profileUser/assets/Fond.jpg";
import fondImageDesktop from "../../components/profileUser/assets/Fond-Large.jpg";

// Key matched from mobile app
const ENCRYPTION_KEY = "MzWNcgKz49Mig0ELPY_GN4SQvpHcqiXv14WQd_CIg1M=";

const QrCodePage = () => {
    const navigate = useNavigate();
    const { userData } = useSession();

    // State
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const qrRef = useRef(null);

    const isTestAccount = useMemo(() => {
        return selectedAccount?.nom === "Guest";
    }, [selectedAccount]);

    // Fetch accounts on mount (similar to mobile fetchAccounts logic)
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                setLoading(true);
                // Using cached accounts from session if available, or fetch fresh
                // The mobile app fetches fresh list. Optimally we use userData.accounts if up to date.
                // But to be consistent with port logic, let's fetch my accounts.

                const response = await comptesApi.getMyAccounts();
                const accs = response.results || [];

                // Normalize accounts similarly to Home.jsx if needed, mainly ensure keys exist
                const normalized = accs.map(acc => ({
                    ...acc,
                    nom: acc.client?.nom || acc.nom || '',
                    prenom: acc.client?.prenom || acc.prenom || '',
                    type_compte: acc.type || acc.type_compte || '',
                    devise: acc.devise || 'HTG'
                }));

                setAccounts(normalized);

                if (normalized.length > 0) {
                    // Default to first account or cached selection logic could go here
                    setSelectedAccount(normalized[0]);
                }
            } catch (error) {
                console.error("Failed to fetch accounts", error);
                // In a real app, show toast error here
            } finally {
                setLoading(false);
            }
        };

        fetchAccounts();
    }, [userData.user]); // Re-fetch if user changes, though unlikely in session

    const handleAccountSelect = (account) => {
        setSelectedAccount(account);
        setModalVisible(false);
    };

    const getQrData = (account) => {
        if (!account) return "";

        const accountData = {
            accountCode: account.code,
            accountId: account.id,
            accountName: `${account.nom || ""} ${account.prenom || ""}`.trim(),
            accountType: account.type_compte || "",
            accountCurrency: account.devise || "",
            timestamp: Date.now() / 1000,
        };

        const jsonData = JSON.stringify(accountData);

        // Encryption Logic
        const keyUtf8 = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
        let keyHex = keyUtf8.toString(CryptoJS.enc.Hex);
        // Pad or trim to 32 bytes (64 hex chars)
        if (keyHex.length < 64) keyHex = keyHex.padEnd(64, "0");
        else if (keyHex.length > 64) keyHex = keyHex.substring(0, 64);

        const key = CryptoJS.enc.Hex.parse(keyHex);
        const encrypted = CryptoJS.AES.encrypt(jsonData, key, {
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7,
        });

        const encryptedBase64 = encrypted.toString();

        const encodedData = {
            url: "https://bousanm.com",
            bsm: encryptedBase64,
        };

        return JSON.stringify(encodedData);
    };

    const handleDownload = () => {
        // Simple canvas download logic for web
        const svg = document.getElementById("qr-code-svg");
        if (svg) {
            const svgData = new XMLSerializer().serializeToString(svg);
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();
            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                const pngFile = canvas.toDataURL("image/png");

                const downloadLink = document.createElement("a");
                downloadLink.download = `qr-${selectedAccount?.code}.png`;
                downloadLink.href = pngFile;
                downloadLink.click();
            };
            img.src = "data:image/svg+xml;base64," + btoa(svgData);
        }
    };

    // Styles reused from mobile (adapted to Tailwind)
    return (
        <div className="min-h-screen relative bg-gray-50 pb-10">
            {/* Background Images */}
            <div
                className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 md:hidden"
                style={{ backgroundImage: `url(${fondImageMobile})` }}
            />
            <div
                className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 hidden md:block"
                style={{ backgroundImage: `url(${fondImageDesktop})` }}
            />

            {/* Header */}
            <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={() => navigate('/home')}
                        className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-blue-600" />
                    </button>
                    <div className="text-center">
                        <h1 className="text-lg font-bold text-slate-800">Mon QR Code</h1>
                        <p className="text-xs text-slate-500">Scannez pour me payer</p>
                    </div>
                    <div className="w-10" /> {/* Spacer */}
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 mt-6">

                {/* Account Selector */}
                <div className="mb-6">
                    <label className="text-xs font-medium text-slate-500 ml-1 mb-2 block uppercase">Compte sélectionné</label>
                    <button
                        onClick={() => setModalVisible(true)}
                        className="w-full bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-blue-100 flex items-center justify-between hover:bg-white transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                                <Wallet className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="text-left">
                                <div className="text-base font-bold text-slate-900">
                                    {selectedAccount ? `#${selectedAccount.code}` : 'Sélectionner un compte'}
                                </div>
                                {selectedAccount && (
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <span className="uppercase">{selectedAccount.devise}</span>
                                        <span>•</span>
                                        <span>{selectedAccount.type_compte}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <ChevronDown className="w-5 h-5 text-blue-600" />
                    </button>
                </div>

                {/* QR Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-3xl p-1 shadow-xl shadow-blue-900/5 mb-6"
                >
                    <div className="bg-gradient-to-b from-white to-slate-50 rounded-[20px] p-6 min-h-[400px] flex flex-col items-center justify-center border border-slate-100">
                        {loading ? (
                            <div className="flex flex-col items-center gap-3">
                                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                                <span className="text-sm text-slate-500">Chargement...</span>
                            </div>
                        ) : !selectedAccount ? (
                            <div className="flex flex-col items-center gap-4 text-center p-8">
                                <QrCodeIcon className="w-16 h-16 text-slate-200" />
                                <span className="text-slate-400 font-medium">Aucun compte sélectionné</span>
                            </div>
                        ) : isTestAccount ? (
                            <div className="flex flex-col items-center gap-4 text-center p-8">
                                <Lock className="w-16 h-16 text-blue-600" />
                                <h3 className="text-lg font-bold text-blue-700">Compte Démo</h3>
                                <p className="text-sm text-slate-500">Ce compte ne peut pas générer de QR Code valide.</p>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center w-full">
                                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-6">
                                    <QRCodeSVG
                                        id="qr-code-svg"
                                        value={getQrData(selectedAccount)}
                                        size={200}
                                        level="H"
                                        includeMargin={false}
                                    // We can't easily use image logo in SVG in react-web without hassle, 
                                    // simpler to overlap a div or just keep it simple.
                                    // For now, simple QR.
                                    />
                                </div>

                                <div className="w-full bg-slate-50 rounded-xl p-3 border border-slate-100 mb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <User className="w-4 h-4 text-slate-400" />
                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Titulaire</span>
                                    </div>
                                    <div className="text-sm font-bold text-slate-900 truncate">
                                        {selectedAccount.nom} {selectedAccount.prenom}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
                                    <ShieldCheck className="w-4 h-4 text-green-600" />
                                    <span className="text-[11px] font-medium text-green-700">QR Code Sécurisé</span>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Actions */}
                {selectedAccount && !loading && !isTestAccount && (
                    <div className="space-y-4">
                        <button
                            onClick={handleDownload}
                            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl py-4 font-bold shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 active:scale-95 transition-transform"
                        >
                            <Download className="w-5 h-5" />
                            Télécharger le QR
                        </button>

                        <div className="bg-blue-50/50 rounded-xl p-4 flex items-start gap-3 border border-blue-100">
                            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-600 leading-relaxed">
                                Ce code QR contient les informations cryptées de votre compte.
                                Partagez-le uniquement avec des personnes de confiance pour recevoir des paiements.
                            </p>
                        </div>
                    </div>
                )}

            </div>

            {/* Account Selection Modal */}
            <AnimatePresence>
                {modalVisible && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setModalVisible(false)}
                            className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 200 }}
                            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[80vh] overflow-hidden flex flex-col"
                        >
                            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0">
                                <div className="flex items-center gap-2">
                                    <Wallet className="w-5 h-5 text-blue-600" />
                                    <h2 className="text-lg font-bold text-slate-800">Choisir un compte</h2>
                                </div>
                                <button onClick={() => setModalVisible(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>

                            <div className="overflow-y-auto p-4 space-y-3 pb-8">
                                {accounts.map(acc => (
                                    <button
                                        key={acc.code}
                                        onClick={() => handleAccountSelect(acc)}
                                        className={`w-full p-4 rounded-xl border flex items-center justify-between transition-all ${selectedAccount?.code === acc.code
                                            ? 'bg-blue-50 border-blue-200 shadow-sm'
                                            : 'bg-white border-gray-100 hover:border-blue-100'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedAccount?.code === acc.code ? 'bg-blue-100' : 'bg-gray-50'
                                                }`}>
                                                <Wallet className={`w-5 h-5 ${selectedAccount?.code === acc.code ? 'text-blue-600' : 'text-gray-400'
                                                    }`} />
                                            </div>
                                            <div className="text-left">
                                                <div className="text-sm font-bold text-slate-900">#{acc.code}</div>
                                                <div className="text-xs text-slate-500">
                                                    {acc.devise} • {acc.type_compte}
                                                </div>
                                            </div>
                                        </div>
                                        {selectedAccount?.code === acc.code && (
                                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                                        )}
                                    </button>
                                ))}
                                {accounts.length === 0 && (
                                    <div className="text-center py-8 text-gray-400">
                                        Aucun compte trouvé
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default QrCodePage;
